import { UseAxiosResult } from 'axios-hooks'
import { useCallback, useEffect, useRef } from 'react'
import { AxiosResponseProtoData } from '../types/types'
import { apiClient, useAxiosClient } from './configs/axios-setup'
import { protoService } from '../proto/ProtoService'
import { cloneWorkflowTask } from '../types/util'

export interface IRequestError {
  status: number
  text: string
}

export const useGetRescaleJobStatus = (): UseAxiosResult => {
  const [{ data, loading, error }, doFetch] = useAxiosClient(
    {
      baseURL: process.env.REACT_APP_RESCALE_API_BASE_URL,
      url: `/jobs/`,
      method: 'GET'
    },
    { manual: true, autoCancel: false, useCache: false }
  )

  return [
    { data, loading, error },
    doFetch,
    () => {
      return
    }
  ]
}

export const useGetSubmissionsByProject = (projectId: string, sumlvl: string): UseAxiosResult => {
  const [{ data, loading, error }, doFetch] = useAxiosClient(
    {
      url: `/WorkflowSubmissions/projectId=${projectId}&_sumlvl=${encodeURIComponent(sumlvl)}`,
      method: 'GET'
    },
    { manual: true, autoCancel: false, useCache: false }
  )

  return [
    { data, loading, error },
    doFetch,
    () => {
      return
    }
  ]
}

export const useGetSubmissions = (): UseAxiosResult => {
  const [{ data, loading, error }, doFetch] = useAxiosClient(
    {
      url: '/WorkflowSubmissions',
      method: 'GET'
    },
    { manual: true, autoCancel: false, useCache: false }
  )

  const doFetchRef = useRef(doFetch)

  useEffect(() => {
    doFetchRef.current = doFetch
  }, [doFetch])

  const refetch = useCallback((query?: any) => {
    return doFetchRef.current({
      url: '/WorkflowSubmissions' + (query ? `?${query}` : '')
    })
  }, [])

  return [
    { data, loading, error },
    refetch,
    () => {
      return
    }
  ]
}

export const useGetSubmission = (): UseAxiosResult<AxiosResponseProtoData> => {
  const [{ data, loading, error }, doFetch] = useAxiosClient(
    {
      method: 'GET'
    },
    { manual: true, autoCancel: false, useCache: false }
  )

  const refetch = id => {
    return doFetch({
      url: `/WorkflowSubmissions/${id}`
    })
  }

  return [
    { data, loading, error },
    refetch,
    () => {
      return
    }
  ]
}

export const useCommitRun = (): UseAxiosResult<AxiosResponseProtoData> => {
  const [{ data, loading, error }, doFetch] = useAxiosClient(
    {
      baseURL: process.env.REACT_APP_WORKFLOW_SUBMISSION_API_BASE_URL,
      method: 'POST'
    },
    { manual: true, autoCancel: false, useCache: false }
  )

  const refetch = runId => {
    return doFetch({
      url: `/WorkflowSubmission/${runId}/workflowSubmit/`
    })
  }

  return [
    { data, loading, error },
    refetch,
    () => {
      return
    }
  ]
}

export const useCancelTask = (): UseAxiosResult<any> => {
  const [{ data, loading, error }, doFetch] = useAxiosClient(
    {
      baseURL: process.env.REACT_APP_WORKFLOW_SUBMISSION_API_BASE_URL,
      method: 'PUT'
    },
    { manual: true, autoCancel: false, useCache: false }
  )

  const refetch = ({ runId, runGroupId, workflowId, taskId }: any) => {
    return doFetch({
      url: `/WorkflowSubmission/${runId}/workflowCancel/?workflowRunGroupSysId=${runGroupId}&workflowSystemRunId=${workflowId}&workflowTaskSystemRunId=${taskId}`
    })
  }

  return [
    { data, loading, error },
    refetch,
    () => {
      return
    }
  ]
}

export const useTimeoutTask = (): UseAxiosResult<any> => {
  const [{ data, loading, error }, doFetch] = useAxiosClient(
    {
      baseURL: process.env.REACT_APP_WORKFLOW_SUBMISSION_API_BASE_URL,
      method: 'PUT'
    },
    { manual: true, autoCancel: false, useCache: false }
  )

  const refetch = ({ gmRefString }: any) => {
    return doFetch({
      url: `/WorkflowSubmission/API/taskTimeoutError/?gmRefString=${encodeURI(gmRefString)}`
    })
  }

  return [
    { data, loading, error },
    refetch,
    () => {
      return
    }
  ]
}

export const useRecommitRun = (): UseAxiosResult<AxiosResponseProtoData> => {
  const [{ data, loading, error }, doFetch] = useAxiosClient(
    {
      baseURL: process.env.REACT_APP_WORKFLOW_SUBMISSION_API_BASE_URL,
      // url: `/WorkflowSubmission/${submissionId}/workflowResubmit/?workflowId=${workflowId}`,
      method: 'PUT'
    },
    { manual: true, autoCancel: false, useCache: false }
  )

  const refetch = ({ runId, workflowRunGroupSysId, workflowSystemRunId, taskId }: any) => {
    let url = `/WorkflowSubmission/${runId}/workflowResubmit/`

    let param
    if (workflowRunGroupSysId) {
      param = `workflowRunGroupSysId=${workflowRunGroupSysId}`
    }
    if (workflowSystemRunId) {
      param = (param ? param + '&' : '') + `workflowSystemRunId=${workflowSystemRunId}`
    }
    if (taskId) {
      param = (param ? param + '&' : '') + `workflowTaskSystemRunId=${taskId}`
    }

    url = `${url}${param ? '?' + param : ''}`

    return doFetch({
      url
    })
  }

  return [
    { data, loading, error },
    refetch,
    () => {
      return
    }
  ]
}

export const useRefreshMethodology = (): UseAxiosResult<any> => {
  const [{ data, loading, error }, doFetch] = useAxiosClient(
    {
      method: 'PUT'
    },
    { manual: true, autoCancel: false, useCache: false }
  )

  const refetch = ({ runId }: any) => {
    return doFetch({
      url: `/WorkflowSubmissions/${runId}/refreshMethodology`
    })
  }

  return [
    { data, loading, error },
    refetch,
    () => {
      return
    }
  ]
}

export const useGetSimTemplates = (): UseAxiosResult<any> => {
  const [{ data, loading, error }, doFetch] = useAxiosClient(
    {
      url: '/WorkflowSubmissions/sim-templates',
      method: 'POST'
    },
    { manual: true, autoCancel: false, useCache: false }
  )

  // Return the raw doFetch so callers can pass `{ url, data }` matching other hooks
  return [{ data, loading, error }, doFetch, () => {}]
}

/**
 * Helper that builds the sim-templates URL for a submission and invokes the provided doFetch.
 * Keeps URL construction centralized so callers only provide the submission id/name and optional query.
 */
export const fetchSimTemplatesForSubmission = (
  doFetch: any,
  submissionId: string,
  submissionName: string,
  q?: string
) => {
  if (!doFetch || !submissionId) {
    return Promise.reject(new Error('Invalid arguments to fetchSimTemplatesForSubmission'))
  }

  const url = `/WorkflowSubmissions/${submissionId}/sim-templates${
    q ? `?q=${encodeURIComponent(q)}` : ''
  }`

  return doFetch({ url, data: { name: submissionName } })
}

export const useAddWorkflowRunGroup = (): UseAxiosResult<AxiosResponseProtoData> => {
  const [{ data, loading, error }, doFetch] = useAxiosClient(
    {
      baseURL: process.env.REACT_APP_WORKFLOW_SUBMISSION_API_BASE_URL,
      // url: `/WorkflowSubmission/${submissionId}/workflowResubmit/?workflowId=${workflowId}`,
      method: 'PUT'
    },
    { manual: true, autoCancel: false, useCache: false }
  )

  const refetch = ({ runId, caseNames, simTemplate, uploadedFile, sourceSystemRunId }: any) => {
    // Trailing slash required by dev environment API gateway
    let url = `/WorkflowSubmissions/${runId}/addWorkflowRunGroup/?caseName=${caseNames.join(',')}`

    // For multi-car drafting runs several run groups share the same case name (one per
    // following car). Passing the source run group's systemRunId (or a list, for bulk resubmit)
    // lets the server clone that exact case (with its selectedFollowingCar) so only the selected
    // following-car position is re-run instead of re-expanding every following car.
    if (sourceSystemRunId) {
      const ids = Array.isArray(sourceSystemRunId) ? sourceSystemRunId.join(',') : sourceSystemRunId
      if (ids) {
        url += `&sourceSystemRunId=${encodeURIComponent(ids)}`
      }
    }

    // If a file was uploaded, send it as FormData
    if (uploadedFile) {
      const formData = new FormData()
      formData.append('simTemplateFile', uploadedFile)

      // Don't set Content-Type header - axios will automatically detect FormData
      // and set it with the proper boundary parameter
      return doFetch({
        url,
        data: formData
      })
    }

    // Otherwise send the selected template metadata
    // Convert protobuf CFDFile to plain object if needed
    let templateData: any = undefined
    if (simTemplate) {
      if (typeof simTemplate.toObject === 'function') {
        templateData = simTemplate.toObject()
      } else {
        templateData = simTemplate
      }
    }

    return doFetch({
      url,
      data: templateData ? { simTemplate: templateData } : undefined
    })
  }

  return [
    { data, loading, error },
    refetch,
    () => {
      return
    }
  ]
}

export const useAddStates = (): UseAxiosResult<AxiosResponseProtoData> => {
  const [{ data, loading, error }, doFetch] = useAxiosClient(
    {
      baseURL: process.env.REACT_APP_WORKFLOW_SUBMISSION_API_BASE_URL,
      method: 'PUT'
    },
    { manual: true, autoCancel: false, useCache: false }
  )

  const refetch = ({ runId, states }: any) => {
    const param = encodeURIComponent(states.join(','))
    return doFetch({
      url: `/WorkflowSubmission/${runId}/updateVehicleStates/?statesToAdd=${param}`
    })
  }

  return [
    { data, loading, error },
    refetch,
    () => {
      return
    }
  ]
}

export const useCreateRun = (): UseAxiosResult => {
  const [{ data, loading, error }, doFetch] = useAxiosClient(
    {
      url: '/WorkflowSubmissions',
      method: 'POST'
    },
    { manual: true, autoCancel: false, useCache: false }
  )

  return [
    { data, loading, error },
    doFetch,
    () => {
      return
    }
  ]
}

export const useUpdateWorkflowSubmission = (id: string): UseAxiosResult => {
  const [{ data, loading, error }, doFetch] = useAxiosClient(
    {
      url: `/WorkflowSubmissions/${id}`,
      method: 'PUT'
    },
    { manual: true, autoCancel: false, useCache: false }
  )

  return [
    { data, loading, error },
    doFetch,
    () => {
      return
    }
  ]
}

export const useUpdateWorkflowSubmissionCompliance = (): UseAxiosResult => {
  const [{ data, loading, error }, doFetch] = useAxiosClient(
    {
      url: ``,
      method: 'PUT'
    },
    { manual: true, autoCancel: false, useCache: false }
  )

  const refetch = ({ runName, team, desc }: any) => {
    return doFetch({
      url: `/WorkflowSubmissions/compliance`,
      data: {
        runName: runName,
        team,
        desc
      }
    })
  }

  return [
    { data, loading, error },
    refetch,
    () => {
      return
    }
  ]
}

export const useUpdateWorkflowSubmissionNotes = (): UseAxiosResult => {
  const [{ data, loading, error }, doFetch] = useAxiosClient(
    {
      url: ``,
      method: 'PUT'
    },
    { manual: true, autoCancel: false, useCache: false }
  )

  const refetch = ({ runName, notes }: any) => {
    return doFetch({
      url: `/WorkflowSubmissions/notes`,
      data: {
        runName: runName,
        notes: notes
      }
    })
  }

  return [
    { data, loading, error },
    refetch,
    () => {
      return
    }
  ]
}

export const useUpdateWorkflowSubmissionRunDescription = (): UseAxiosResult => {
  const [{ data, loading, error }, doFetch] = useAxiosClient(
    {
      url: ``,
      method: 'PUT'
    },
    { manual: true, autoCancel: false, useCache: false }
  )

  const refetch = ({ runName, description }: any) => {
    return doFetch({
      url: `/WorkflowSubmissions/description`,
      data: {
        runName: runName,
        description: description
      }
    })
  }

  return [
    { data, loading, error },
    refetch,
    () => {
      return
    }
  ]
}

export const useGetComplianceCaseCount = (): UseAxiosResult => {
  const [{ data, loading, error }, doFetch] = useAxiosClient(
    {
      url: ``,
      method: 'GET'
    },
    { manual: true, autoCancel: false, useCache: false }
  )

  const refetch = ({ runName }: any) => {
    return doFetch({
      url: `/WorkflowSubmissions/compliance/case-count?runName=${encodeURIComponent(runName)}`
    })
  }

  return [
    { data, loading, error },
    refetch,
    () => {
      return
    }
  ]
}

export const useUpdateWorkflowSubmissionVehicleStates = (id: string): UseAxiosResult => {
  const [{ data, loading, error }, doFetch] = useAxiosClient(
    {
      url: `/WorkflowSubmissions/${id}/states`,
      method: 'PUT'
    },
    { manual: true, autoCancel: false, useCache: false }
  )

  const refetch = ({ runId, data }: any) => {
    return doFetch({
      url: `/WorkflowSubmissions/${runId}/states`,
      data
    })
  }

  return [
    { data, loading, error },
    refetch,
    () => {
      return
    }
  ]
}

export const useGetRunConfigCase = (): UseAxiosResult => {
  const [{ data, loading, error }, doFetch] = useAxiosClient(
    {
      method: 'GET'
    },
    { manual: true, autoCancel: false, useCache: false }
  )

  const refetch = (id: string) => {
    return doFetch({
      url: `/WorkflowSubmissions/${id}/cases`
    })
  }

  return [
    { data, loading, error },
    // @ts-ignore
    refetch,
    () => {
      return
    }
  ]
}

export const useAddRunConfigCase = (): UseAxiosResult => {
  const [{ data, loading, error }, doFetch] = useAxiosClient(
    {
      method: 'POST'
    },
    { manual: true, autoCancel: false, useCache: false }
  )

  const refetch = ({ id, data }: any) => {
    return doFetch({
      url: `/WorkflowSubmissions/${id}/cases`,
      data
    })
  }

  return [
    { data, loading, error },
    refetch,
    () => {
      return
    }
  ]
}

export const useDeleteRunConfigCase = (): UseAxiosResult => {
  const [{ data, loading, error }, doFetch] = useAxiosClient(
    {
      method: 'DELETE'
    },
    { manual: true, autoCancel: false, useCache: false }
  )

  const refetch = ({ id, caseName, caseKey }: any) => {
    const query = caseKey ? `?key=${encodeURIComponent(caseKey)}` : ''
    return doFetch({
      url: `/WorkflowSubmissions/${id}/cases/${caseName}${query}`,
      data
    })
  }

  return [
    { data, loading, error },
    refetch,
    () => {
      return
    }
  ]
}

export const useUpdateRunConfigCase = (): UseAxiosResult => {
  const [{ data, loading, error }, doFetch] = useAxiosClient(
    {
      method: 'PUT'
    },
    { manual: true, autoCancel: false, useCache: false }
  )

  const refetch = ({ id, data }: any) => {
    return doFetch({
      url: `/WorkflowSubmissions/${id}/cases`,
      data
    })
  }

  return [
    { data, loading, error },
    refetch,
    () => {
      return
    }
  ]
}

export async function getSubmissionStatus(id: string) {
  const res = await apiClient.get(`/WorkflowSubmissions/${id}`)
  return res
}

/**
 * Parsed metadata for an image filename
 */
export interface PostProcImageMetadata {
  imageType: string | null // Surface or Sect
  section: string | null // X, Y, Z (for Sect type)
  location: string | null // Top, Bottom, Left, Right, Front, Aft, BottomDiffuser, BottomSplitter (for Surface type)
  metricType: string | null // Cp, CpT, dCp, WSS, dWSS, WSSOIL, Vort, Vr, RMSdCp, etc.
  index: number | null
  value: number | null
  referenceRunId?: string | null // Reference case id for compare_pictures deltas (e.g., '323')
}

/**
 * Image metadata returned from post-proc images API
 */
export interface PostProcImage {
  name: string
  fullPath: string
  url: string
  size: number
  lastModified: string
  contentType: string
  metadata: PostProcImageMetadata
}

/**
 * Available filter options for post-proc images
 */
export interface PostProcImageFilters {
  imageTypes: string[] // e.g., ['Sect', 'Surface']
  sections: string[] // e.g., ['X', 'Y', 'Z']
  locations: string[] // e.g., ['Top', 'Bottom', 'Left', 'Right', 'Front', 'Aft', 'BottomDiffuser', 'BottomSplitter']
  metricTypes: string[] // e.g., ['Cp', 'CpT', 'dCp', 'WSS', 'dWSS', 'Vort', 'Vr']
  // Distinct (referenceRunId, metricType) combos from compare_pictures deltas, e.g. { referenceRunId: '323', metricType: 'dCp' }
  referenceMetrics?: Array<{ referenceRunId: string; metricType: string }>
}

/**
 * Response from post-proc images API
 */
export interface PostProcImagesResponse {
  success: boolean
  systemRunId: string
  images: PostProcImage[]
  totalCount: number
  filteredCount: number
  hasMore: boolean
  skip: number
  limit: number
  availableFilters: PostProcImageFilters
  error?: string
  message?: string
}

/**
 * Filter parameters for fetching post-proc images
 */
export interface PostProcImageQueryParams {
  skip?: number
  limit?: number
  imageType?: string // Surface or Sect
  section?: string // X, Y, Z
  location?: string // Top, Bottom, Left, Right, Front, Aft, BottomDiffuser, BottomSplitter
  metricType?: string // Cp, CpT, dCp, WSS, dWSS, Vort, Vr, etc.
  referenceRunId?: string // Reference case id for compare deltas, or 'none' for baseline-only images
  // Multi-select metrics. Each entry is a plain metric or "{metric}||{referenceRunId}" compare delta;
  // entries are OR'd server-side and take precedence over metricType/referenceRunId.
  metricFilters?: string[]
  search?: string // Filename search
}

/**
 * Apply a multi-select metric selection to a post-proc images query string.
 * An empty selection means "All Metrics", which is restricted to baseline (non-compare) images.
 */
export const applyMetricFilterParams = (params: URLSearchParams, selected?: string[] | null) => {
  const values = (selected ?? []).filter(Boolean)
  if (values.length) params.set('metricFilters', values.join(','))
  else params.set('referenceRunId', 'none')
  return params
}

/**
 * Hook to get post-processing images for a case
 * Supports pagination and filtering by imageType/section/location/metric type
 */
export const useGetPostProcImages = (): [
  { data: PostProcImagesResponse | undefined; loading: boolean; error: any },
  (systemRunId: string, params?: PostProcImageQueryParams) => Promise<any>,
  () => void
] => {
  const [{ data, loading, error }, doFetch] = useAxiosClient<PostProcImagesResponse>(
    {
      url: '/WorkflowSubmissions',
      method: 'GET'
    },
    { manual: true, autoCancel: false, useCache: false }
  )

  const fetchImages = (systemRunId: string, params: PostProcImageQueryParams = {}) => {
    const {
      skip = 0,
      limit = 50,
      imageType,
      section,
      location,
      metricType,
      referenceRunId,
      metricFilters,
      search
    } = params
    const queryParams = new URLSearchParams()
    queryParams.set('skip', String(skip))
    queryParams.set('limit', String(limit))
    if (imageType) queryParams.set('imageType', imageType)
    if (section) queryParams.set('section', section)
    if (location) queryParams.set('location', location)
    if (metricType) queryParams.set('metricType', metricType)
    if (referenceRunId) queryParams.set('referenceRunId', referenceRunId)
    if (metricFilters?.length) queryParams.set('metricFilters', metricFilters.join(','))
    if (search) queryParams.set('search', search)

    return doFetch({
      url: `/WorkflowSubmissions/cases/${encodeURIComponent(
        systemRunId
      )}/images?${queryParams.toString()}`
    }).catch(err => {
      // Error is already captured in the error state, just prevent unhandled rejection
      console.warn('Failed to fetch post-proc images:', err?.response?.data?.message || err.message)
      return err
    })
  }

  return [
    { data, loading, error },
    fetchImages,
    () => {
      return
    }
  ]
}

/**
 * Fire-and-forget call to process solve counts for a completed submission.
 * Scans Quick Step Logs in Azure Blob and increments resubmitAttempts for cases with completed solves.
 */
export const processSolveCount = (submissionId: string): Promise<void> => {
  return apiClient
    .post(`/WorkflowSubmissions/${submissionId}/processSolveCount`)
    .then(res => {
      console.log('[processSolveCount] Processed:', res.data)
    })
    .catch(err => {
      console.warn('[processSolveCount] Failed:', err?.response?.data || err.message)
    })
}

/**
 * Same as processSolveCount but returns the full response payload for logging/debugging.
 */
export const processSolveCountWithResult = (submissionId: string): Promise<any> => {
  return apiClient
    .post(`/WorkflowSubmissions/${submissionId}/processSolveCount`)
    .then(res => res.data)
    .catch(err => {
      console.warn('[processSolveCountWithResult] Failed:', err?.response?.data || err.message)
      throw err
    })
}

/**
 * Record a SIM file change event for a specific case within a submission.
 * Requires a reason to be provided.
 */
export const addSimChangeMetadata = (
  submissionId: string,
  record: {
    caseName: string
    previousSimFile: string
    newSimFile: string
    reason: string
    systemRunId?: string
  }
): Promise<any> => {
  return apiClient
    .post(`/WorkflowSubmissions/${submissionId}/sim-change-metadata`, record)
    .then(res => res.data)
}

/**
 * Retrieve all SIM file change records for a submission.
 */
export const getSimChangeMetadata = (submissionId: string): Promise<any[]> => {
  return apiClient
    .get(`/WorkflowSubmissions/${submissionId}/sim-change-metadata`)
    .then(res => res.data)
}

export const useUpdateReferenceRuns = (): UseAxiosResult => {
  const [{ data, loading, error }, doFetch] = useAxiosClient(
    {
      method: 'PUT'
    },
    { manual: true, autoCancel: false, useCache: false }
  )

  const refetch = ({ submissionId, systemRunId, referenceRuns }: any): any => {
    // TODO: update endpoint path once backend is implemented
    return doFetch({
      url: `/WorkflowSubmissions/${submissionId}/referenceRuns`,
      data: { systemRunId, referenceRuns }
    })
  }

  return [
    { data, loading, error },
    refetch,
    () => {
      return
    }
  ]
}

export const useAddWorkflowTask = (): UseAxiosResult<AxiosResponseProtoData> => {
  const [{ data, loading, error }, doFetch] = useAxiosClient(
    {
      baseURL: process.env.REACT_APP_WORKFLOW_SUBMISSION_API_BASE_URL,
      method: 'PUT'
    },
    { manual: true, autoCancel: false, useCache: false }
  )

  const refetch = ({ runId, workflowRunGroupSysId, workflowSystemRunId, workflowTask }: any) => {
    // Trailing slash required by dev environment API gateway
    let url = `/WorkflowSubmissions/${runId}/addWorkflowTask/?workflowRunGroupSysId=${workflowRunGroupSysId}&workflowSystemRunId=${workflowSystemRunId}`

    // Need to encode the workflowTask
    const encodedWTProto = workflowTask
      ? protoService.encodePayload(cloneWorkflowTask(workflowTask), 'workflowtask')
      : undefined

    return doFetch({
      url,
      data: encodedWTProto
    })
  }

  return [
    { data, loading, error },
    refetch,
    () => {
      return
    }
  ]
}

/**
 * Plain (non-hook) call to append reference run entries to a specific workflowRunGroup's
 * runGroupCase.referenceRuns. Used for background/automatic syncing (e.g. auto-populating
 * reference runs from a run's referenceRunName on submit) where hook state isn't convenient.
 */
export const updateReferenceRunsRequest = (
  submissionId: string,
  systemRunId: string,
  referenceRuns: string[]
): Promise<any> => {
  return apiClient
    .put(`/WorkflowSubmissions/${submissionId}/referenceRuns`, { systemRunId, referenceRuns })
    .then(res => res.data)
}
